import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';

import { Stack, Typography, Grid } from "@mui/material";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import {PrimaryButton, BreadCrumbs, LoadingBar, DeleteModal, ActionBox, UITable} from "../Common";
import { DetailPanel, DetailPanelData } from "../Common/DetailPanel";
import { Pill } from "../Common/Pill";

import { Grey1, Grey3, toolTipBlue, trakrefBlue } from "../../color_utils";
import { trakrefFontFamily } from "../../utils";

import Routes from '../../routes';
import UserInfo from '../../UserInfo';
import useNavBar from '../../context/NavBar/useNavBar';
import { useNavigate, useBreadCrumbs }from '../../hooks';
import { setFiltersPushUser, convertBoolean } from "../Common/DataGrid/utils";
import { trakrefSettingsToggles, findWeightUnitOption, CULTURE_DISPLAY, CULTURE_NOTE } from "./utils";

const initialValues = {
    instanceId: null,
    name: null,
    shortName: null,
    accountType: null,
    industryType: null,
    energyStarPartnerType: null,
    leakRateCalculationType: null,
    street1: null,
    street2: null,
    city: null,
    postalCode: null,
    state: null,
    country: null,
    title: null,
    firstName: null,
    lastName: null,
    mainPhone: null,
    email: null,
    alternatePhone: null,
    alternatePhoneExt: null,
    mainPhoneExt: null,
    fax: null,
    logo: null
};

const columns = [
    { id: 'Name', title: "Asset Type", property: "name", key: "name" },
    { id: 'Count', title: "Total Assets", property: "count", key: "count" },
    { id: 'RetrofittedCount', title: "Total Retrofitted Assets", property: "retrofittedCount" },
];

const AccountDetails = ({ match }) => {
    // Set when a System Admin opens an account from Manage Accounts; otherwise this shows the session account
    const viewAccountId = match?.params?.id;
    const isOtherAccount = !!viewAccountId && Number(viewAccountId) !== Number(UserInfo.instanceId);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [accountDetails, setAccountDetaiils] = useState(initialValues);
    const [actionBoxItems, setActionBoxItems] = useState([]);
    const [showHiddenFields, setShowHiddenFields] = useState(false);
    const renderSubscriptionInformation = (
        UserInfo.isSystemAdministrator()
        || UserInfo.isSuperTenantAdmin()
        || UserInfo.isTenantAdministrator()
    );


    const { refreshInstance } = useNavBar();
    const navigate = useNavigate({
        pageName:'AccountDetails'
    });
    // The GCC view uses its own fixed trail, so it must not register a "Manage Your Account" crumb
    const breadCrumbs = useBreadCrumbs({
        path: viewAccountId ? null : { id: 'AccountDetails', text: 'Manage Your Account', link: window.location.pathname }
    });

    const onDeleteAccount = async () => {
        try {
            const response = await fetch(Routes.Account.DeleteAccount(accountDetails.instanceId), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                const data = await response.json();
                refreshInstance(data.instanceId, data.optedIn);
            }
        }
        catch {
            console.error("something went wrong!");
        }
    }

    const fetchAccountDetails = useCallback(async () => {
        try {
            setLoading(true);

            const response = await fetch(Routes.Account.GetAccountDetails(false, viewAccountId));
            const data = await response.json();

            if (response.status !== 200) {
                return;
            }

            setAccountDetaiils({
                instanceId: data.instanceId,
                name: data.name,
                shortName: data.shortName,
                accountType: data.accountType,
                industryType: data.industryType,
                energyStarPartnerType: data.energyStarPartnerType,
                leakRateCalculationType: data.leakRateCalculationType,
                street1: data.street1 ? data.street1 : "",
                street2: data.street2 ? data.street2 : "",
                city: data.city,
                postalCode: data.postalCode,
                state: data.state,
                country: data.country,
                title: data.title,
                firstName: data.firstName,
                lastName: data.lastName,
                mainPhone: data.mainPhone,
                email: data.email,
                alternatePhone: data.alternatePhone,
                alternatePhoneExt: data.alternatePhoneExt,
                mainPhoneExt: data.mainPhoneExt,
                fax: data.fax,
                logo: data.logo,
                createDate: data.createDate,
                totalUsers: data.totalUsers,
                totalInHouseTechs: data.totalInHouseTechs,
                locationUsage: data.locationUsage,
                totalServiceProviders: data.totalServiceProviders,
                assetUsage: data.assetUsage,
                totalServiceProvidersUsers: data.totalServiceProvidersUsers,
                serviceLocations: data.serviceLocations,
                warehouse: data.warehouse,
                equipmentLocations: data.equipmentLocations,
                wholeSaleSupplier: data.wholeSaleSupplier,
                technicianTruck: data.technicianTruck,
                totalLocations: data.totalLocations,
                allAssets: data.allAssets,
                // Trakref Settings + Default User Settings (#17118)
                ...Object.fromEntries(trakrefSettingsToggles.map(({ name }) => [name, !!data[name]])),
                weightUnits: data.weightUnitId
            });
        }
        catch {
            setLoading(false);
        }
        finally {
            setLoading(false);
        }
    }, [UserInfo.instanceId, viewAccountId]);
    
    const handleLocationTypeClick = (value) => {
        const field = "locationType";
        
        const locationsTableState = {
            filter: { filterModel: { items: [{ field, value }] } },
        };
        
        return setFiltersPushUser({
            destinationTableUrl: Routes.Location.LocationTable(),
            destinationPath: '/Locations',
            items: [{ field, value }],
            navigate,
        });
    };

    useEffect(() => {
        fetchAccountDetails();
    }, [fetchAccountDetails]);

    useEffect(() => {
        const items = [];

        // Not offered on the GCC view: Manage Accounts had no delete path, and it would drop the admin back to their native account
        if (UserInfo.isSystemAdministrator() && !viewAccountId) {
            items.push({
                Icon: DeleteOutlineOutlinedIcon,
                title: "Delete Account",
                onClick: () => setShowDeleteModal(true)
            });
        }

        setActionBoxItems(items);
    }, [viewAccountId]);

    const PanelHeader = ({ title }) => (
        <Typography
            variant="h5"

            style={{ color: Grey1, fontSize: '16px' }}
        >
            {title}
        </Typography>
    );

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <BreadCrumbs
                path={viewAccountId ? [
                    { text: "Geek Command Center", to: "/GeekCommandCenter" },
                    { text: "Manage Accounts", to: "/ManageAccounts" },
                    { text: accountDetails.name || "Account Details" }
                ] : breadCrumbs.getSortedBreadCrumbs()}
            />

            <Stack
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                sx={{ gap: "20px", marginLeft: "14px" }}>
                {accountDetails.logo ? (
                    <img className="greenImageSmall" src={accountDetails.logo} />
                ) : (
                    <div className="greenCircleSmall">
                        {accountDetails.shortName}
                    </div>
                )}
                <Typography
                    variant="h5"
                    sx={{
                        "&.MuiTypography-root": {
                            fontFamily: trakrefFontFamily,
                            fontSize: "22px",
                        },
                    }}
                    style={{ color: Grey1 }}
                >
                    {accountDetails.name}
                </Typography>

                <Pill text={accountDetails.accountType} bgColor={toolTipBlue} />

                <div style={{ marginLeft: "auto", marginRight: "10px" }}>
                    <PrimaryButton
                        buttonText="Edit"
                        onClick={() => navigate.push(viewAccountId ? `/ManageAccounts/EditAccount/${viewAccountId}` : "/EditAccountDetails")}
                    />
                </div>
            </Stack>

            <Grid container marginTop={"0.4rem"} spacing={1}>
                <Grid item sm={10} md={10} lg={10}>
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px"
                    }}>
                        <DetailPanel expandable={false}>
                            <DetailPanelData
                                title="Industry"
                                data={accountDetails.industryType}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Enery star partner type"
                                data={accountDetails.energyStarPartnerType}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Leak rate calculation type"
                                data={accountDetails.leakRateCalculationType}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Address"
                                data={`${accountDetails.street1} ${accountDetails.street2}`}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="City"
                                data={accountDetails.city}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="State"
                                data={accountDetails.state}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Zip Code"
                                data={accountDetails.postalCode}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Country"
                                data={accountDetails.country}
                                link={false}
                                 
                            />
                        </DetailPanel>

                        <Grid item style={{ paddingLeft: '2.2%' }}>
                            <PanelHeader title="Primary Contact" />
                        </Grid>

                        <DetailPanel expandable={false}>
                            <DetailPanelData
                                title="TITLE"
                                data={accountDetails.title}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Email"
                                data={accountDetails.email}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Alternate Phone number"
                                data={accountDetails.alternatePhone}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="First name"
                                data={accountDetails.firstName}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Main Phone number"
                                data={accountDetails.mainPhone}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Alternate Phone extension"
                                data={accountDetails.alternatePhoneExt}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Last name"
                                data={accountDetails.lastName}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Main Phone Extension"
                                data={accountDetails.mainPhoneExt}
                                link={false}
                                 
                            />
                            <DetailPanelData
                                title="Fax"
                                data={accountDetails.fax}
                                link={false}

                            />
                        </DetailPanel>

                        {/* #17118: read-only settings, same audience as Subscription Information; Edit stays the only way to change them */}
                        {renderSubscriptionInformation && (
                            <>
                                <Grid item style={{ paddingLeft: '2.2%' }}>
                                    <PanelHeader title="Trakref Settings" />
                                </Grid>

                                <DetailPanel expandable={false} autoHeight>
                                    {trakrefSettingsToggles.map(({ name, label }) => (
                                        <DetailPanelData
                                            key={name}
                                            title={label}
                                            data={convertBoolean(accountDetails[name])}
                                        />
                                    ))}
                                </DetailPanel>

                                <Grid item style={{ paddingLeft: '2.2%' }}>
                                    <PanelHeader title="Default User Settings" />
                                </Grid>

                                <DetailPanel expandable={false}>
                                    <DetailPanelData
                                        title="Weight Units"
                                        data={findWeightUnitOption(accountDetails.weightUnits)?.name ?? accountDetails.weightUnits ?? null}
                                    />
                                    <DetailPanelData
                                        title="Culture"
                                        data={
                                            <>
                                                {CULTURE_DISPLAY}
                                                <Typography component="span" variant="caption" sx={{ display: "block", color: Grey3, fontFamily: trakrefFontFamily }}>
                                                    {CULTURE_NOTE}
                                                </Typography>
                                            </>
                                        }
                                    />
                                </DetailPanel>
                            </>
                        )}

                        {renderSubscriptionInformation && (
                            <>
                                <Grid item sx={{ paddingLeft: '2.2%', display: "flex", flexDirection: "row" }}>
                                    <PanelHeader title="Subscription Information" />
                                    <p
                                        style={{
                                            color: trakrefBlue,
                                            textAlign: "right",
                                            fontFamily: trakrefFontFamily,
                                            fontSize: "14px",
                                            fontStyle: "normal",
                                            fontWeight: 600,
                                            lineHeight: "normal",
                                            letterSpacing: "1.2px",
                                            cursor: "pointer",
                                            marginLeft: "auto",
                                            marginRight: "4%"
                                        }}
                                        onClick={() => setShowHiddenFields(x => !x)}
                                    >
                                        {showHiddenFields ? "Show Less" : "View More"}
                                    </p>
                                </Grid>

                                <DetailPanel expandable={false}>
                                    <DetailPanelData
                                        title="Account Creation Date"
                                        data={accountDetails.createDate && moment(accountDetails.createDate).format('MM/DD/YYYY') || null}
                                    />
                                    <DetailPanelData
                                        title="Total Users"
                                        data={accountDetails.totalUsers}
                                    />
                                    <DetailPanelData
                                        title="Total In-House Techs"
                                        data={accountDetails.totalInHouseTechs}
                                    />
                                    <DetailPanelData
                                        title="Location Usage"
                                        data={accountDetails.locationUsage}
                                    />
                                    <DetailPanelData
                                        title="Total Service Providers"
                                        data={accountDetails.totalServiceProviders}
                                    />
                                    <DetailPanelData
                                        title="Asset Usage"
                                        data={accountDetails.assetUsage}
                                    />
                                    <DetailPanelData
                                        title="Total Service Providers Users"
                                        data={accountDetails.totalServiceProvidersUsers}
                                    />
                                </DetailPanel>

                                {showHiddenFields && (
                                    <>
                                        <Grid item sx={{ paddingLeft: '2.2%' }}>
                                            <PanelHeader title="Locations By Type" />
                                        </Grid>
                                        {/* Counts link to /Locations for the session account, so they stay plain text when viewing another account */}
                                        <DetailPanel expandable={false}>
                                            <DetailPanelData
                                                link={!isOtherAccount}
                                                isClickable={!isOtherAccount}
                                                onClick={() => handleLocationTypeClick("Service Location")}
                                                title="Service Locations"
                                                data={accountDetails.serviceLocations}
                                            />
                                            <DetailPanelData
                                                link={!isOtherAccount}
                                                isClickable={!isOtherAccount}
                                                onClick={() => handleLocationTypeClick("Warehouse")}
                                                title="Warehouse"
                                                data={accountDetails.warehouse}
                                            />
                                            <DetailPanelData
                                                link={!isOtherAccount}
                                                isClickable={!isOtherAccount}
                                                onClick={() => handleLocationTypeClick("Equipment Location")}
                                                title="Equipment Location"
                                                data={accountDetails.equipmentLocations}
                                            />
                                            <DetailPanelData
                                                link={!isOtherAccount}
                                                isClickable={!isOtherAccount}
                                                onClick={() => handleLocationTypeClick("Wholesaler/Supplier")}
                                                title="Wholesale / Supplier"
                                                data={accountDetails.wholeSaleSupplier}
                                            />
                                            <DetailPanelData
                                                link={!isOtherAccount}
                                                isClickable={!isOtherAccount}
                                                onClick={() => handleLocationTypeClick("Technician/Truck")}
                                                title="Technician / Truck"
                                                data={accountDetails.technicianTruck}
                                            />
                                            <DetailPanelData
                                                title="Total Locations"
                                                data={accountDetails.totalLocations}
                                            />
                                        </DetailPanel>
                                        {accountDetails.allAssets && (
                                            <Grid item sx={{ paddingLeft: '2.2%' }}>
                                                <PanelHeader title="Assets By Type" />
                                            </Grid>
                                        )}
                                        {accountDetails.allAssets && (
                                            <UITable
                                                data={accountDetails.allAssets}
                                                columns={columns}
                                                url="accountDetails"
                                                noResultText="No results to display. Please use the filters about to generate your report."
                                            />
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </Grid>
                <Grid item sm={2} md={2} lg={2}>
                    {
                        !!actionBoxItems.length && (
                            <ActionBox
                                items={actionBoxItems}
                                sx={{ height: 'fit-content !important' }}
                            />
                        )
                    }
                </Grid>
            </Grid>


            <DeleteModal
                open={showDeleteModal}
                buttonText={"Yes, Delete"}
                title="Delete Account?"
                description="Are you sure you want to delete the account?"
                onClose={() => setShowDeleteModal(false)}
                onConfirm={onDeleteAccount}
            />
        </>
    );
};

export default AccountDetails;
